import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  getPriceAlerts,
  createPriceAlert,
  updatePriceAlert,
  deletePriceAlert,
  hasAlertForProduct,
  getPriceHistory,
  type AlertType,
} from '@/data/priceAlerts';
import { toast } from 'sonner';

/**
 * Hook to fetch user's price alerts
 */
export function usePriceAlerts() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['price-alerts', user?.id],
    queryFn: () => getPriceAlerts(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to check if product has active alert
 */
export function useHasAlert(productId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['has-alert', user?.id, productId],
    queryFn: () => hasAlertForProduct(user!.id, productId!),
    enabled: !!user?.id && !!productId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch price history
 */
export function usePriceHistory(productId: string | undefined) {
  return useQuery({
    queryKey: ['price-history', productId],
    queryFn: () => getPriceHistory(productId!),
    enabled: !!productId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to create price alert
 */
export function useCreatePriceAlert() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      productId: string;
      targetPrice: number;
      alertType?: AlertType;
    }) =>
      createPriceAlert({
        userId: user!.id,
        ...params,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['has-alert'] });
      toast.success('Price alert created');
    },
    onError: (error: any) => {
      if (error.code === '23505') {
        toast.error('You already have an alert for this product');
      } else {
        toast.error('Failed to create price alert');
      }
    },
  });
}

/**
 * Hook to update price alert
 */
export function useUpdatePriceAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      alertId: string;
      target_price?: number;
      alert_type?: AlertType;
      is_active?: boolean;
    }) => updatePriceAlert(params.alertId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['has-alert'] });
      toast.success('Price alert updated');
    },
    onError: () => {
      toast.error('Failed to update price alert');
    },
  });
}

/**
 * Hook to delete price alert
 */
export function useDeletePriceAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePriceAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['has-alert'] });
      toast.success('Price alert deleted');
    },
    onError: () => {
      toast.error('Failed to delete price alert');
    },
  });
}
